import React from 'react';
import { Helmet } from '@lib/helmet-shim';
import { getRobotsMeta, SEO_CONFIG, siteConfig } from '../config';

interface SeoHeadProps {
  title?: string;
  description?: string;
  pageSlug?: string; // 'terms', 'dmca', 'privacy', etc.
  image?: string;
  url?: string;
  canonicalUrl?: string;
  jsonLd?: Record<string, unknown>;
}

/**
 * SEO Head Component
 * Handles all meta tags including dynamic robots meta for different page types
 * Automatically applies noindex,nofollow for terms/dmca/privacy pages
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  pageSlug,
  image = SEO_CONFIG.DEFAULT_IMAGE,
  url,
  canonicalUrl,
  jsonLd
}) => {
  const robotsMeta = getRobotsMeta(pageSlug);
  const fullTitle = title ? `${title}${SEO_CONFIG.TITLE_SEPARATOR}${SEO_CONFIG.TITLE_SUFFIX}` : undefined;
  const canonical = canonicalUrl || (typeof window !== 'undefined' ? window.location.href : siteConfig.siteUrl);

  return (
    <Helmet>
      <script
        async
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-9215675649531263"
        crossOrigin="anonymous"
      />
      {fullTitle && <title>{fullTitle}</title>}
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robotsMeta} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteConfig.siteName} />
      {fullTitle && <meta property="og:title" content={fullTitle} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={SEO_CONFIG.OG_LOCALE} />
      <meta property="og:url" content={canonical} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={SEO_CONFIG.TWITTER_CARD_TYPE} />
      {fullTitle && <meta name="twitter:title" content={fullTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
